/* What a contract's lifecycle means on screen, decided once.
 *
 * **A pending contract is not incomplete — it is waiting on a named person.** That is the judgement
 * the whole screen turns on: the sign-off columns name whoever still owes a signature rather than
 * showing a dash, because "who has not signed" is the question they answer. `retired` is muted rather
 * than red, because a retired contract is a record of what was promised, not a failure.
 */

import type {
  Contract,
  ContractState,
  ContractWrite,
  ObservedFacts,
  SignoffSide,
} from '@/shared/api/generated/contracts';

export type StateTone = 'ok' | 'pending' | 'muted' | 'draft';

const TONES: Record<ContractState, StateTone> = {
  draft: 'draft',
  pending_signoff: 'pending',
  active: 'ok',
  retired: 'muted',
};

export function toneOf(state: ContractState): StateTone {
  return TONES[state];
}

export function stateLabelKey(state: ContractState): string {
  return `contracts.state.${state}`;
}

/** The sentence under the state chip: what this state *means* for evaluation.
 *
 * On the page rather than in a tooltip, because it is the sentence that stops somebody reading
 * `draft` as broken and `pending_signoff` as stuck. Nothing evaluates in either.
 */
export function stateHintKey(state: ContractState): string {
  return `contracts.stateHint.${state}`;
}

export const SEVERITY_TONE = {
  info: 'info',
  warning: 'warn',
  critical: 'error',
} as const satisfies Record<Contract['breach_severity'], 'info' | 'warn' | 'error'>;

/* ---------------------------------------------------------------- the ledger */

export type StateFilter = ContractState | 'all';

export const STATE_FILTERS: readonly StateFilter[] = [
  'all',
  'active',
  'pending_signoff',
  'draft',
  'retired',
];

export function matchesFilter(contract: Contract, filter: StateFilter): boolean {
  return filter === 'all' || contract.state === filter;
}

/* There is deliberately no ordering helper here. `GET /v1/contracts` is already
 * `ORDER BY created_at DESC`, so a second sort in the client would be a second answer to "what
 * order is this ledger in" — and the two would disagree the day either changes. The server's is the
 * one that also decides what a page boundary contains.
 */

/** The terms a contract actually carries, as label keys — never the nulls. */
export function termKeysOf(contract: Contract): string[] {
  const keys: string[] = [];
  if (contract.terms.min_quality_score !== null) keys.push('contracts.term.score');
  if (contract.terms.max_assessment_age_seconds !== null)
    keys.push('contracts.term.assessment');
  if (contract.terms.pinned_schema_version !== null) keys.push('contracts.term.schema');
  return keys;
}

/* ---------------------------------------------------------------- one side's signature */

export interface Signoff {
  readonly side: SignoffSide;
  readonly signedBy: string | null;
  readonly signedAt: string | null;
  /** The party who owes the signature — shown whether or not it has been given. */
  readonly subject: string;
}

export function signoffsOf(contract: Contract): readonly Signoff[] {
  return [
    {
      side: 'producer',
      signedBy: contract.producer_signed_by,
      signedAt: contract.producer_signed_at,
      subject: contract.producer_subject,
    },
    {
      side: 'consumer',
      signedBy: contract.consumer_signed_by,
      signedAt: contract.consumer_signed_at,
      subject: contract.consumer_subject,
    },
  ];
}

/** Which sides are still outstanding, as copy keys for the sentence under the ledger. */
export function outstandingSides(contract: Contract): readonly SignoffSide[] {
  return signoffsOf(contract)
    .filter((each) => each.signedBy === null)
    .map((each) => each.side);
}

/* ---------------------------------------------------------------- terms against facts */

export type TermKind = 'score' | 'assessment' | 'schema';

/** What the last evaluation found for one term, in the three states it genuinely has.
 *
 * **`absent` is not `measured` with an empty string, and neither is `unevaluated`.** A dataset that
 * was never scored, a contract nobody has evaluated, and a score of 0.0 are three different facts,
 * and collapsing any pair of them is the satisfied-by-absence inversion this platform refuses
 * everywhere else. The absent case carries a copy key rather than a sentence, so the reason is
 * translated with the rest of the screen.
 */
export type Observed =
  | { readonly state: 'unevaluated' }
  | { readonly state: 'absent'; readonly key: string }
  | { readonly state: 'measured'; readonly text: string };

export interface TermRow {
  readonly kind: TermKind;
  /** The promised value, already a string — a score, a duration or a version. */
  readonly promised: string;
  readonly observed: Observed;
  /** `null` until an evaluation has run: unevaluated is not "holds". */
  readonly holds: boolean | null;
}

const TERM_CAUSE: Record<TermKind, string> = {
  score: 'min_quality_score',
  assessment: 'max_assessment_age_seconds',
  schema: 'pinned_schema_version',
};

/** A duration as the reference writes it: whole days, whole hours, else seconds. */
export function asAge(seconds: number): string {
  if (seconds % 86400 === 0) return `${String(seconds / 86400)}d`;
  if (seconds % 3600 === 0) return `${String(seconds / 3600)}h`;
  return `${String(seconds)}s`;
}

/** The Promised / Observed / Holds rows for one contract against one evaluation.
 *
 * **`observed` and `holds` say "not evaluated" rather than "holds" when nothing has run.** An
 * unevaluated contract has made no promise anybody has checked, and rendering a tick would claim a
 * measurement that does not exist.
 *
 * A breach is matched by its `term`, which the backend sets to the term's own field name — so a row
 * is broken because the evaluator said that term broke, never because a comparison was re-derived
 * here. Two answers to "did this term hold" is how a screen comes to disagree with the incident it
 * links to.
 */
export function termRows(
  contract: Contract,
  evaluation: {
    readonly breaches: readonly { readonly term: string }[];
    readonly observed: ObservedFacts;
  } | null,
): readonly TermRow[] {
  const broken = new Set((evaluation?.breaches ?? []).map((each) => each.term));

  const held = (kind: TermKind): boolean | null =>
    evaluation === null ? null : !broken.has(TERM_CAUSE[kind]);

  /* One place decides the three states, so a new term cannot accidentally render an absent fact as
   * a measured one. */
  const seen = (value: string | number | null, absent: string): Observed =>
    evaluation === null
      ? { state: 'unevaluated' }
      : value === null
        ? { state: 'absent', key: absent }
        : { state: 'measured', text: String(value) };

  const rows: TermRow[] = [];
  if (contract.terms.min_quality_score !== null) {
    rows.push({
      kind: 'score',
      promised: `≥ ${String(contract.terms.min_quality_score)}`,
      observed: seen(evaluation?.observed.latest_score ?? null, 'contracts.absent.score'),
      holds: held('score'),
    });
  }
  if (contract.terms.max_assessment_age_seconds !== null) {
    rows.push({
      kind: 'assessment',
      promised: `≤ ${asAge(contract.terms.max_assessment_age_seconds)}`,
      observed: seen(
        evaluation?.observed.last_assessed_at ?? null,
        'contracts.absent.assessment',
      ),
      holds: held('assessment'),
    });
  }
  if (contract.terms.pinned_schema_version !== null) {
    const version = evaluation?.observed.current_schema_version ?? null;
    rows.push({
      kind: 'schema',
      promised: `v${String(contract.terms.pinned_schema_version)}`,
      observed:
        version === null
          ? seen(null, 'contracts.absent.schema')
          : seen(`v${String(version)}`, 'contracts.absent.schema'),
      holds: held('schema'),
    });
  }
  return rows;
}

/* ---------------------------------------------------------------- what may be done */

export type ContractAction =
  'submit' | 'sign_producer' | 'sign_consumer' | 'activate' | 'evaluate' | 'retire';

export interface Offered {
  readonly action: ContractAction;
  /** The filled control. Exactly one per state — the step the contract is waiting for. */
  readonly primary: boolean;
}

/** The actions this contract's *state* admits, before any permission is consulted.
 *
 * Two separate questions, kept separate: the state decides what is possible and `can()` decides what
 * this caller may be offered. Folding them would make an unpermitted control indistinguishable from
 * an impossible one, and the screen says different things about each.
 *
 * **Activation is its own step and is offered once both signatures are in.** An earlier version of
 * this function omitted it on the belief that the server activates a contract when its second
 * signature lands. It does not — `sign` records a signature and returns a contract still
 * `pending_signoff`, and `POST /activate` is a separate call that spends the approval. Withholding
 * the control left a fully signed contract with no next step on the screen at all: the one state in
 * which the lifecycle is waiting on a person, and the button they need was the one missing.
 */
export function offeredFor(contract: Contract): readonly Offered[] {
  const unsigned = new Set(contract.unsigned_sides);
  switch (contract.state) {
    case 'draft':
      return [{ action: 'submit', primary: true }];
    case 'pending_signoff':
      if (unsigned.size === 0) return [{ action: 'activate', primary: true }];
      return [
        ...(unsigned.has('producer')
          ? [{ action: 'sign_producer' as const, primary: true }]
          : []),
        ...(unsigned.has('consumer')
          ? [{ action: 'sign_consumer' as const, primary: !unsigned.has('producer') }]
          : []),
      ];
    case 'active':
      return [
        { action: 'evaluate', primary: true },
        { action: 'retire', primary: false },
      ];
    case 'retired':
      return [];
  }
}

/** What is deliberately not offered here, and why — rendered as a disabled chip with the reason.
 *
 * Named rather than absent: a missing Evaluate on a retired contract reads as an oversight, where
 * "a retired contract is not evaluated" is the platform explaining itself.
 */
export function blockedFor(contract: Contract): readonly string[] {
  switch (contract.state) {
    case 'draft':
      return ['contracts.blocked.sign'];
    case 'retired':
      return ['contracts.blocked.evaluate'];
    case 'pending_signoff':
      return ['contracts.blocked.evaluatePending'];
    case 'active':
      return [];
  }
}

/* ---------------------------------------------- offered, withheld, impossible */

/** The state-admitted actions, split by whether this caller may be offered them.
 *
 * **Three outcomes rather than two, and the third is the one that was missing.** An action can be
 * impossible (the state does not admit it — that is `blockedFor`), withheld (the state admits it and
 * this caller holds no grant), or offered. The screen rendered the first and the third and dropped
 * the second silently, so an approver looking at a draft saw an empty action row and could not tell
 * "nothing to do here" from "not yours to do" — which is exactly the pair this platform insists must
 * not look alike.
 *
 * Holding the grant is still not a promise the server will accept: a signature is refused from
 * anyone but the declared subject for that side, with a 403, even for an admin. The control is an
 * offer and never a claim about who may sign.
 */
export interface Gated {
  readonly offered: readonly Offered[];
  readonly withheld: readonly ContractAction[];
}

export function gateFor(
  contract: Contract,
  permitted: (action: ContractAction) => boolean,
): Gated {
  const admitted = offeredFor(contract);
  return {
    offered: admitted.filter((each) => permitted(each.action)),
    withheld: admitted
      .filter((each) => !permitted(each.action))
      .map((each) => each.action),
  };
}

/* ---------------------------------------------- authoring a draft */

/** How stale an assessment may be, in the two units anybody actually states it in.
 *
 * Seconds is what the wire takes and nobody thinks in: `86400` is a promise a reader has to decode
 * before they can check it. The conversion happens here so the drawer never holds a number whose
 * unit is implied by a sibling field.
 */
export type AgeUnit = 'hours' | 'days';

const SECONDS: Record<AgeUnit, number> = { hours: 3600, days: 86400 };

export interface DraftForm {
  readonly datasetId: string;
  readonly name: string;
  readonly producer: string;
  readonly consumer: string;
  readonly useScore: boolean;
  readonly score: string;
  readonly useAge: boolean;
  readonly age: string;
  readonly ageUnit: AgeUnit;
  readonly useSchema: boolean;
  readonly schema: string;
  readonly severity: Contract['breach_severity'];
}

/** **Severity defaults to warning, and the default is the decision.** Defaulting to critical teaches
 * people to ignore critical, which spends the one severity that should interrupt somebody. */
export const EMPTY_DRAFT: DraftForm = {
  datasetId: '',
  name: '',
  producer: '',
  consumer: '',
  useScore: true,
  score: '',
  useAge: false,
  age: '',
  ageUnit: 'hours',
  useSchema: false,
  schema: '',
  severity: 'warning',
};

export const SEVERITIES: readonly Contract['breach_severity'][] = [
  'info',
  'warning',
  'critical',
];

export const TERM_KINDS: readonly TermKind[] = ['score', 'assessment', 'schema'];

/** A draft that is ready to send, or the copy key naming what is not.
 *
 * **Checked here, and the server still checks.** These are refusals the author can see and fix in
 * the drawer — no dataset, two identical parties, no term at all — and making somebody submit to
 * learn them is a round trip that teaches nothing. The server's guards stay the authority: a
 * same-party contract is refused there whatever this says.
 *
 * A ticked term with an empty box is `null` rather than an error, and that distinction matters:
 * ticking "Minimum quality score" and typing nothing promises nothing, while sending `0` would
 * promise a floor of zero — a term that can never breach, recorded as though somebody chose it.
 */
export type DraftCheck =
  | { readonly ok: true; readonly body: ContractWrite }
  | { readonly ok: false; readonly problem: string };

export function checkDraft(form: DraftForm): DraftCheck {
  const name = form.name.trim();
  const producer = form.producer.trim();
  const consumer = form.consumer.trim();

  if (form.datasetId === '') return { ok: false, problem: 'contracts.new.needDataset' };
  if (name === '') return { ok: false, problem: 'contracts.new.needName' };
  if (producer === '' || consumer === '')
    return { ok: false, problem: 'contracts.new.needParties' };
  if (producer === consumer)
    return { ok: false, problem: 'contracts.new.samePartyError' };

  const promised = (use: boolean, raw: string): number | null => {
    if (!use) return null;
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const score = promised(form.useScore, form.score);
  const age = promised(form.useAge, form.age);
  const schema = promised(form.useSchema, form.schema);

  /* At least one *promised* term, never one ticked box. A tick with an empty box promises nothing,
   * and the server refuses a contract whose three terms are all null — so the message names the real
   * problem rather than letting a filled-looking form come back as a 422. */
  if (score === null && age === null && schema === null)
    return { ok: false, problem: 'contracts.new.needTerm' };

  return {
    ok: true,
    body: {
      dataset_id: form.datasetId,
      name,
      producer_subject: producer,
      consumer_subject: consumer,
      terms: {
        min_quality_score: score,
        max_assessment_age_seconds: age === null ? null : age * SECONDS[form.ageUnit],
        pinned_schema_version: schema,
      },
      breach_severity: form.severity,
    },
  };
}
